import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useGoogleFonts } from '@/hooks/useGoogleFonts';
import ArrowDown from '@/images/ArrowDown.svg';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import styled from '@emotion/styled';
import { Autocomplete, CircularProgress, Popper, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ElementType } from 'polotno/model/group-model';

const Container = styled.div`
    position: relative;

    .bp5-popover2-target {
        border: ${({ theme }) => `1px solid ${theme.colors.black[200]}`};
        border-radius: 8px;
        max-height: 32px !important;
        outline: none;

        .bp5-button.bp5-minimal {
            max-height: 32px !important;
            margin-right: 0 !important;
            outline: none;

            .bp5-button-text {
                font-size: 14px !important;
                font-weight: 400 !important;
            }

            .bp5-icon.bp5-icon-caret-down {
                display: none;
            }

            :after {
                content: '';
                background: ${() => `url(${ArrowDown})`};
                width: 16px;
                height: 16px;
                background-size: contain;
            }

            :active,
            :hover,
            &.bp5-active {
                background-color: ${({ theme }) => theme.colors.black[100]} !important;
            }
        }
    }
`;

const StyledTextField = styled(TextField)`
    .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary.MuiInputBase-fullWidth {
        width: 150px;
        padding-right: 40px;
        padding: 0 10px 0 8px;
        cursor: pointer;

        :hover {
            background-color: ${({ theme }) => theme.colors.black[100]};
        }
    }

    input {
        color: ${({ theme }) => theme.colors.secondary[900]};
        font-size: 14px !important;
        padding: 6px 4px 6px 5px;
        cursor: pointer;
    }

    fieldset {
        border-radius: 8px;
        border: ${({ theme }) => `1px solid ${theme.colors.black[200]}`} !important;
    }
`;

const CustomPopper = styled(Popper)({
    width: 'auto !important',
});

export const FontFamilyControl = observer(() => {
    const { store } = useAdvancedEditorContext();
    const googleFontsQuery = useGoogleFonts();
    const elements = store.selectedElements;
    const element = elements?.[0] as ElementType & { fontFamily: number };

    const handleChange = (fontFamily: string | null) => {
        if (!fontFamily) return;
        elements.forEach(element => element.set({ fontFamily }));
    };

    return (
        <Container>
            <Autocomplete
                options={googleFontsQuery.data ?? []}
                loading={googleFontsQuery.isLoading}
                value={element?.fontFamily ? element.fontFamily : undefined}
                onChange={(_, newValue) => handleChange(newValue as string | null)}
                disabled={googleFontsQuery.isError}
                renderInput={params => (
                    <StyledTextField
                        {...params}
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <div
                                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                    onClick={e => {
                                        params.inputProps.onMouseDown?.(e as any);
                                    }}>
                                    {googleFontsQuery.isLoading ? <CircularProgress color="primary" size={20} /> : null}
                                    <MaskedIcon src={ArrowDown} size="16px" />
                                </div>
                            ),
                        }}
                    />
                )}
                slotProps={{
                    popper: {
                        sx: {
                            '& .MuiAutocomplete-listbox > li': {
                                height: '2.25em',
                            },
                        },
                    },
                }}
                PopperComponent={props => <CustomPopper {...props} placement="bottom-end" />}
            />
        </Container>
    );
});
